<template>
  <!-- The game chat (SPEC §5.6): on a phone a window rising from the bottom of the screen, on a
       desktop a panel beside the board (or in the replay's side panel). -->
  <transition :name="layout === 'sheet' ? 'chat-slide' : ''">
    <div v-if="chat.shown" class="game-chat" :class="layout" :style="frameStyle" role="dialog" :aria-label="t('chat-title')"
         @touchstart="touchStart" @touchmove="touchMove">
      <div class="chat-head">
        <span class="chat-title">{{ t('chat-title') }}</span>
        <button type="button" class="chat-close" :aria-label="t('chat-close')" :title="t('chat-close')" @click="chat.close()">×</button>
      </div>
      <div ref="list" class="chat-list" @scroll="onScroll" @click="closeEmoji">
        <div v-if="chat.loading" class="chat-note">{{ t('page-loading') }}</div>
        <button v-else-if="chat.loadFailed" type="button" class="chat-note chat-retry" @click="chat.load(false)">{{ t('chat-load-failed') }}</button>
        <div v-else-if="!rows.length" class="chat-note chat-empty">{{ t('chat-empty') }}</div>
        <template v-for="r in rows">
          <div v-if="r.divider" :key="'d' + r.key" ref="divider" class="chat-divider"><span>{{ t('chat-new') }}</span></div>
          <div :key="r.key" class="chat-message" :class="{ mine: r.mine, pending: r.pending }">
            <div v-if="r.meta" class="chat-meta">
              <span v-if="!r.mine">{{ r.name }}</span><span v-if="r.spectator">{{ t('chat-spectator') }}</span><template v-if="!r.mine"> · </template>
              <button v-if="canJump" type="button" class="chat-move" @click="$emit('jump', r.moveCount)">{{ t('chat-move', { n: r.moveCount }) }}</button>
              <span v-else class="chat-move">{{ t('chat-move', { n: r.moveCount }) }}</span>
              · {{ r.time }}
            </div>
            <div class="chat-text" :class="{ 'chat-big': r.big }">{{ r.text }}</div>
            <button v-if="r.failed" type="button" class="chat-failed" :title="r.failed" @click="chat.send(null, r.p)">{{ t('chat-send-failed') }}</button>
          </div>
        </template>
      </div>
      <form class="chat-input" @submit.prevent="send" @keydown.esc="closeEmoji">
        <!-- On a phone the open panel takes the keyboard's place, so the button then brings the
             keyboard back (⌨); on a desktop it only shows and hides the panel. -->
        <button type="button" class="chat-emoji-button" :class="{ keyboard: keyboardButton }"
                :aria-label="t(keyboardButton ? 'chat-keyboard-open' : 'chat-emoji-open')" :title="t(keyboardButton ? 'chat-keyboard-open' : 'chat-emoji-open')"
                :aria-expanded="String(emojiShown)" @mousedown.prevent @click="toggleEmoji">{{ keyboardButton ? '⌨' : '😊' }}</button>
        <!-- On a phone with the panel open the field keeps the focus, and so its own cursor, with
             inputmode="none": focused, but without the keyboard (owner, 2026-11-03). iPhones show the
             keyboard all the same, so there the field lets go of the focus and the page draws its
             cursor (chat-caret). -->
        <span class="chat-field">
          <input ref="input" v-model="draft" type="text" maxlength="500" :placeholder="t('chat-placeholder')" enterkeyhint="send"
                 @blur="keepSelection" @click="onFieldTap" @keydown.esc="closeEmoji" @scroll="drawnCaretAt" />
          <span v-if="drawnCaret !== null" class="chat-caret" :style="{ left: drawnCaret + 'px' }" aria-hidden="true"></span>
        </span>
        <button type="submit" class="btn-main chat-send" :disabled="!draft.trim()">{{ t('button-chat-send') }}</button>
      </form>
      <!-- mousedown.prevent: tapping the panel doesn't take the focus (and the cursor) from the field. -->
      <chat-emoji-panel v-if="emojiShown" :layout="layout" @insert="insert" @delete="deleteBack"
                        @mousedown.native.prevent @keydown.native.esc="closeEmoji" />
    </div>
  </transition>
</template>

<script>
import { t } from '@/texts';
import ChatEmojiPanel from '@/components/ChatEmojiPanel.vue';

// A message of one to three emoji and nothing else is shown big (plan 2026-11-02-chat-emoji-voice.md).
// Built at run time so a browser without Unicode property escapes just shows them normally.
// iPhones and iPads (iPadOS calls itself a Mac with a touch screen) ignore inputmode="none" for the
// keyboard (owner's test, 2026-11-03); nothing tells a page so, hence asking the browser's name.
function isIos() {
  const n = typeof navigator === 'undefined' ? {} : navigator;
  return /iPhone|iPad|iPod/.test(n.userAgent || '') || (n.platform === 'MacIntel' && n.maxTouchPoints > 1);
}

let bigEmoji = null;
try {
  const one = '\\p{Extended_Pictographic}(?:\\uFE0F|\\u20E3|[\\u{1F3FB}-\\u{1F3FF}]|\\u200D\\p{Extended_Pictographic}\\uFE0F?)*';
  bigEmoji = new RegExp(`^(?:${one}\\s*){1,3}$`, 'u');
} catch (e) { /* old browser */ }

function pad(n) { return String(n).padStart(2, '0'); }

// A message's time: the time alone today, 昨天 and the time yesterday, else the date and time.
function timeText(iso) {
  const d = new Date(iso), now = new Date();
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const day = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86400000);
  if (days === 0) return time;
  if (days === 1) return t('chat-time-yesterday', { time });
  return t('chat-time-date', { month: d.getMonth() + 1, day: d.getDate(), time });
}

export default {
  name: 'GameChat',

  components: { ChatEmojiPanel },

  props: {
    chat: { type: Object, required: true },
    // 'sheet' (phone) or 'panel' (desktop).
    layout: { type: String, required: true },
    // Where the frame sits: the sheet's top, or the panel's box (page coordinates).
    frame: { type: Object, default: () => ({}) },
    // The ids of the players (the others are spectators).
    playerIds: { type: Array, default: () => [] },
    // In a finished game's replay a message's move label moves the board there.
    canJump: Boolean,
  },

  data() {
    // selection: where the field's cursor was when it last lost focus, for inserting from the panel.
    // drawnCaret: where the page draws the cursor (px from the field's left), or null (iPhone, panel open).
    return { draft: '', atBottom: true, emojiShown: false, selection: null, drawnCaret: null };
  },

  computed: {
    frameStyle() {
      const px = v => (v === undefined ? undefined : `${v}px`);
      if (this.layout === 'sheet') return { top: px(this.frame.top) };
      return { left: px(this.frame.left), top: px(this.frame.top), width: px(this.frame.width), height: px(this.frame.height) };
    },

    rows() {
      const rows = this.chat.messages.map(m => ({
        key: `n${m.n}`,
        mine: m.user.id === this.chat.me,
        name: m.user.displayName,
        spectator: !this.playerIds.includes(m.user.id),
        moveCount: m.moveCount,
        time: timeText(m.time),
        text: m.text,
        big: !!(bigEmoji && bigEmoji.test(m.text)),
        meta: true,
        divider: this.chat.dividerAfter !== null && m.n === this.firstNew,
      }));
      for (const p of this.chat.pending) {
        if (p.n !== null && this.chat.messages.some(m => m.n === p.n)) continue;
        rows.push({ key: `p${p.local}`, mine: true, pending: !p.failed, text: p.text, big: !!(bigEmoji && bigEmoji.test(p.text)), failed: p.failed, p, meta: false });
      }
      return rows;
    },

    // Phone, panel open: the field keeps the focus without the keyboard (true), or lets go of it and
    // the page draws the cursor (false: iPhones).
    keepFocus() {
      return this.layout === 'sheet' && !isIos();
    },

    drawCaret() {
      return this.layout === 'sheet' && isIos();
    },

    keyboardButton() {
      return this.layout === 'sheet' && this.emojiShown;
    },

    firstNew() {
      const m = this.chat.messages.find(x => x.n > this.chat.dividerAfter && x.user.id !== this.chat.me);
      return m ? m.n : null;
    },
  },

  watch: {
    'chat.shown'(shown) {
      if (shown) this.$nextTick(() => this.scrollOnOpen());
      else this.emojiShown = false;
    },
    emojiShown(shown) {
      // Phone, the panel closed other than by ⌨ (sending, a tap on the messages, Esc): the field gets
      // its keyboard back for next time and lets go of the focus, so a tap on it brings the keyboard.
      if (!shown) this.drawnCaret = null;
      if (shown || this.layout !== 'sheet') return;
      if (this.toKeyboard) { this.toKeyboard = false; return; }
      const input = this.$refs.input;
      if (!input) return;
      input.setAttribute('inputmode', 'text');
      if (document.activeElement === input) input.blur();
    },
  },

  // Whatever changed (a message arrived, a sent one got its time line, the panel opened or closed),
  // a list that was at its end stays at its end, so the latest message is always whole in view.
  updated() {
    if (this.atBottom) this.scrollToBottom();
  },

  mounted() {
    if (this.chat.shown) this.$nextTick(() => this.scrollOnOpen());
  },

  methods: {
    t,

    scrollOnOpen() {
      const list = this.$refs.list;
      if (!list) return;
      const divider = this.$refs.divider && this.$refs.divider[0];
      if (divider) list.scrollTop = Math.max(0, divider.offsetTop - list.offsetTop - 8);
      else this.scrollToBottom();
      this.onScroll();
    },

    scrollToBottom() {
      const list = this.$refs.list;
      if (list) list.scrollTop = list.scrollHeight;
      this.atBottom = true;
    },

    onScroll() {
      const list = this.$refs.list;
      if (list) this.atBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 40;
    },

    // On a phone, a finger moving over the chat window must never scroll the game page under it
    // (owner, 2026-11-02): the list scrolls while it can, the phrase row sideways, nothing else moves.
    touchStart(e) {
      const p = e.touches && e.touches[0];
      this.touch = p ? { x: p.clientX, y: p.clientY } : null;
    },

    touchMove(e) {
      if (this.layout !== 'sheet' || !this.touch || !e.cancelable) return;
      const p = e.touches[0];
      const dx = p.clientX - this.touch.x, dy = p.clientY - this.touch.y;
      this.touch = { x: p.clientX, y: p.clientY };
      const sideways = Math.abs(dx) > Math.abs(dy);
      const target = e.target instanceof Element ? e.target : null;
      // The phrase row and the field may move sideways (scrolling the row, moving the cursor).
      if (sideways && target && target.closest('.chat-phrases, input')) return;
      const list = this.$refs.list;
      if (!sideways && list && target && list.contains(target)) {
        const top = list.scrollTop <= 0;
        const bottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
        if ((dy > 0 && !top) || (dy < 0 && !bottom)) return;
      }
      e.preventDefault();
    },

    keepSelection() {
      const input = this.$refs.input;
      // Called as the field loses focus, when its cursor is still there.
      if (input) this.selection = [input.selectionStart, input.selectionEnd];
    },

    closeEmoji() {
      this.emojiShown = false;
    },

    // Phone: the field keeps the focus but the keyboard goes down (inputmode none), or the other way.
    // The mode only takes effect when the field gets the focus, hence blur, then focus, at once
    // (inside the tap, as phones only raise or lower the keyboard for a tap).
    refocus(mode) {
      const input = this.$refs.input;
      if (!input) return;
      const [from, to] = this.cursor();
      input.blur();
      input.setAttribute('inputmode', mode);
      input.focus();
      try { input.setSelectionRange(from, to); } catch (e) { /* not focusable */ }
    },

    toggleEmoji() {
      const input = this.$refs.input;
      if (this.emojiShown) {
        this.emojiShown = false;
        if (this.keepFocus) { this.toKeyboard = true; this.refocus('text'); }
        else if (this.drawCaret && input) {
          this.toKeyboard = true;
          const [from, to] = this.cursor();
          input.focus();
          try { input.setSelectionRange(from, to); } catch (e) { /* not focusable */ }
        }
        return;
      }
      this.emojiShown = true;
      if (this.keepFocus) this.refocus('none');
      else if (this.drawCaret && input) {
        if (document.activeElement === input) input.blur();   // the keyboard goes down
        this.$nextTick(() => this.revealCursor());
      }
    },

    // Phone, panel open: a tap on the field brings the keyboard back, as ⌨ does.
    onFieldTap() {
      if (this.layout === 'sheet' && this.emojiShown) this.toggleEmoji();
    },

    cursor() {
      const input = this.$refs.input;
      if (input && document.activeElement === input) return [input.selectionStart, input.selectionEnd];
      const n = this.draft.length;
      if (!this.selection) return [n, n];
      return [Math.min(this.selection[0], n), Math.min(this.selection[1], n)];
    },

    // A phrase or emoji from the panel goes into the field at the cursor, replacing a selection.
    insert(text) {
      const [from, to] = this.cursor();
      const next = this.draft.slice(0, from) + text + this.draft.slice(to);
      if (next.length > 500) return;
      this.draft = next;
      this.afterPanelEdit(from + text.length);
    },

    // ⌫: the selection, or the one character (a whole emoji) before the cursor.
    deleteBack() {
      let [from, to] = this.cursor();
      if (from === to) {
        const before = Array.from(this.draft.slice(0, from));
        if (!before.length) return;
        let drop = before.pop();
        // An emoji's variation selector or skin tone goes with the emoji before it.
        while (before.length && /^(?:\uFE0F|\uD83C[\uDFFB-\uDFFF])$/.test(drop)) drop = before.pop() + drop;
        from -= drop.length;
      }
      this.draft = this.draft.slice(0, from) + this.draft.slice(to);
      this.afterPanelEdit(from);
    },

    // The field's own cursor goes after what was put in, and the field scrolls to it, as typing would.
    afterPanelEdit(at) {
      this.selection = [at, at];
      this.$nextTick(() => {
        const input = this.$refs.input;
        if (!input) return;
        if (this.drawCaret) { this.revealCursor(); return; }
        if (document.activeElement !== input && this.layout !== 'sheet') input.focus();
        try { input.setSelectionRange(at, at); } catch (e) { /* not focusable */ }
        if (at >= this.draft.length) input.scrollLeft = input.scrollWidth;
      });
    },

    // iPhone, panel open: scroll the field so the cursor is in view, as typing would, and draw it.
    // The text is measured on a canvas in the field's font (an element on the page was drawn larger
    // than the field on the owner's iPhone, 2026-11-03). Once the text is wider than the field, the
    // field's own width of it corrects the measure, and a cursor at the end is put exactly at the
    // end. The cursor never leaves the field.
    revealCursor() {
      const input = this.$refs.input;
      if (!input || !this.emojiShown || !this.drawCaret) return;
      const style = getComputedStyle(input);
      const padLeft = parseFloat(style.paddingLeft) || 0, padRight = parseFloat(style.paddingRight) || 0;
      if (!this.measure) this.measure = document.createElement('canvas').getContext('2d');
      const ctx = this.measure;
      ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const at = this.cursor()[0];
      const all = ctx.measureText(this.draft).width;
      const before = ctx.measureText(this.draft.slice(0, at)).width;
      const room = input.clientWidth - padLeft - padRight;
      const native = input.scrollWidth - padLeft - padRight;   // the text's width as the field draws it, once wider than the field
      const overflowing = input.scrollWidth > input.clientWidth + 1;
      let x;
      if (at >= this.draft.length) x = padLeft + (overflowing ? native : Math.min(all, room));
      else x = padLeft + (overflowing && all > 0 ? before * native / all : before);
      const right = input.clientWidth - padRight;
      if (x - input.scrollLeft > right) input.scrollLeft = x - right;
      else if (x - input.scrollLeft < padLeft) input.scrollLeft = Math.max(0, x - padLeft);
      this.cursorX = x;
      this.drawnCaretAt();
    },

    drawnCaretAt() {
      const input = this.$refs.input;
      if (!input || !this.emojiShown || !this.drawCaret || this.cursorX === undefined) { this.drawnCaret = null; return; }
      const style = getComputedStyle(input);
      const lo = parseFloat(style.paddingLeft) || 0, hi = input.clientWidth - (parseFloat(style.paddingRight) || 0);
      this.drawnCaret = input.offsetLeft + Math.min(hi, Math.max(lo, this.cursorX - input.scrollLeft)) - 1;
    },

    send() {
      const text = this.draft.trim();
      if (!text) return;
      this.emojiShown = false;
      this.selection = null;
      this.draft = '';
      this.atBottom = true;
      this.chat.send(text);
    },
  },
};
</script>

<style scoped>
.game-chat {
  /* Quick taps on the emoji must not zoom the page (owner, 2026-11-03). */
  touch-action: manipulation;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: #fff;
  overflow: hidden;
  text-align: left;
  z-index: 50;
}

/* Phone: rises from the bottom of the screen, 16 px in from each side (SPEC §5.6). */
.game-chat.sheet {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 0;
  border-radius: 14px 14px 0 0;
  box-shadow: 0 -3px 16px rgba(0, 0, 0, .25);
}

.game-chat.panel {
  position: absolute;
  border: 1px solid #c8b28a;
  border-radius: 10px;
}

.chat-slide-enter-active { transition: transform 150ms ease-out; }
.chat-slide-leave-active { transition: transform 120ms ease-in; }
.chat-slide-enter, .chat-slide-leave-to { transform: translateY(100%); }
@media (prefers-reduced-motion: reduce) {
  .chat-slide-enter-active, .chat-slide-leave-active { transition: none; }
}

.chat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: none;
  padding: 4px 6px 4px 12px;
  border-bottom: 1px solid #eee;
}

.chat-title { font-size: 16px; color: #2c3e50; }

.chat-close {
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: none;
  color: #888;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
}

.chat-list {
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 12px 8px;
  -webkit-overflow-scrolling: touch;
}

/* A short conversation sits at the bottom, by the input, as in messaging apps. */
.chat-list > :first-child { margin-top: auto; }

.chat-note {
  display: block;
  margin: 24px auto;
  border: none;
  background: none;
  color: #999;
  font-size: 14px;
  text-align: center;
}

.chat-retry { color: #d00; cursor: pointer; }

.chat-message {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 6px 0;
}

.chat-message.mine { align-items: flex-end; }

.chat-meta {
  margin: 0 4px 2px;
  font-size: 12px;
  color: #999;
}

.chat-move {
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
}

button.chat-move { text-decoration: underline; cursor: pointer; }

.chat-text {
  max-width: 78%;
  padding: 7px 11px;
  border-radius: 12px;
  border-top-left-radius: 3px;
  background: #f2f2f2;
  color: #2c3e50;
  font-size: 15px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.mine .chat-text {
  border-top-left-radius: 12px;
  border-top-right-radius: 3px;
  background: #f3e6d3;
}

.pending .chat-text { opacity: .55; }

.chat-failed {
  margin: 2px 4px 0;
  padding: 0;
  border: none;
  background: none;
  color: #d00;
  font-size: 12px;
  cursor: pointer;
}

.chat-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0;
  color: #0175ff;
  font-size: 12px;
}

.chat-divider::before, .chat-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #bcd7ff;
}

.chat-input {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
  padding: 8px 10px;
  border-top: 1px solid #eee;
}

.chat-field {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
}

.chat-caret {
  position: absolute;
  top: 9px;
  bottom: 9px;
  width: 2px;
  background: #0175ff;   /* the field's own cursor has the same colour (caret-color) */
  pointer-events: none;
  animation: chat-blink 1s steps(1) infinite;
}

@keyframes chat-blink { 50% { opacity: 0; } }

.chat-input input {
  caret-color: #0175ff;
  flex: 1;
  min-width: 0;
  height: 38px;
  box-sizing: border-box;
  padding: 0 10px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 16px;
}

.chat-emoji-button {
  flex: none;
  width: 34px;
  height: 38px;
  margin: 0 -4px 0 -2px;
  padding: 0;
  border: none;
  background: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.chat-emoji-button.keyboard { color: #666; font-size: 26px; }

/* Only emoji (one to three): big and without a bubble, as messaging apps do. */
.chat-text.chat-big,
.mine .chat-text.chat-big {
  padding: 0 2px;
  background: none;
  font-size: 40px;
  line-height: 1.15;
}

.chat-send {
  flex: none;
  height: 38px;
  margin: 0;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 15px;
}
</style>
